import {logger, Util} from 'yougile-util'
import {DesktopRenderer} from '../app/client/desktop-renderer'
import {NotyNotify, NotyRing} from '../app/types/noty'
import {Backend} from './backend'

declare class Notification {
  public static permission: string
  public static requestPermission: () => void

  constructor(title: string, opts: unknown)

  public onclick(): void

  public onclose(): void

  public close(): void
}

const sounds: {[key: string]: HTMLAudioElement} = {
  chat1: new Audio('/media/audio/chat1.mp3'),
  chat2: new Audio('/media/audio/chat2.mp3'),
  chat3: new Audio('/media/audio/chat3.mp3'),
  chat4: new Audio('/media/audio/chat4.mp3'),
  chat5: new Audio('/media/audio/chat5.mp3'),
  chat6: new Audio('/media/audio/chat6.mp3'),
  long: new Audio('/media/audio/long.mp3'),
  ringPomodoro: new Audio('/media/audio/ring-pomodoro.mp3')
}

const lastPlayed: {[key: string]: number} = {
  chat1: 0,
  chat2: 0,
  chat3: 0,
  long: 0
}

const staticTitle = document.title
let titleBlinkHandlers: TimeoutHandler[] = [] // setInterval ids

let tabActive = true

const onActivate: Set<() => void> = new Set()
const onBlur: Set<() => void> = new Set()

window.addEventListener('focus', () => {
  tabActive = true
  onActivate.forEach((func: () => void) => func())
})
window.addEventListener('blur', () => {
  tabActive = false
  onBlur.forEach((func: () => void) => func())
})

const openNotifications: Map<string, Notification> = new Map()

/**
 * Provides simple methods to manage in-browser notifications.
 * browser push notifications
 */
export class Notifications {
  public static askPermissions(): void {
    if (typeof Notification === 'undefined') {
      return
    }
    if (Notification.permission !== 'granted') {
      Notification.requestPermission()
    }
  }

  public static notify: NotyNotify = (
    title: string,
    icon = '/favicon.ico',
    body: string,
    onClick?: () => void,
    withBlink = true,
    onlyWhenHidden = true
  ) => {
    if (withBlink) {
      Notifications.titleBlinkIfTabInactive([
        '[ ! ! ! ! ! ! ! ! ! ! ]',
        '[ New message ]'
      ])
    }

    if (
      (!onlyWhenHidden || document.hidden || !document.hasFocus()) &&
      typeof Notification !== 'undefined' &&
      Notification.permission !== 'denied'
    ) {
      if (Notification.permission !== 'granted') {
        Notifications.askPermissions()
      }
      DesktopRenderer.notify()
      if (!icon) {
        icon = '/favicon.ico'
      }
      const n = new Notification(title, {
        icon: Backend.toDataUrl(icon),
        body,
        sticky: true
      })
      const notificationId = Util.UUID()
      openNotifications.set(notificationId, n)

      n.onclick = () => {
        n.close()
        openNotifications.forEach(notif => notif.close())
        openNotifications.clear()
        window.focus()
        if (Util.isDesktop()) {
          DesktopRenderer.focus()
        }
        if (onClick) {
          setTimeout(() => onClick(), 500)
        }
      }
      const h1 = setTimeout(() => n.close(), 3 * 3600 * 1000)
      n.onclose = () => {
        clearTimeout(h1)
        openNotifications.delete(notificationId)
      }
    }
  }

  public static ring: NotyRing = (name: string) => {
    const now = Date.now()
    if (now - lastPlayed[name] < 2000) {
      logger.log('Load', name)
      sounds[name].load()
    }
    lastPlayed[name] = now
    sounds[name].play().catch(e => {
      // see https://stackoverflow.com/questions/64929753/play-failed-because-the-user-didnt-interact-with-the-document-first-issue
      logger.error((e as Error).message)
    })
  }

  public static titleClearBlink(): void {
    document.title = staticTitle
    titleBlinkHandlers.forEach(h => clearInterval(h))
    titleBlinkHandlers = []
  }

  private static titleBlink(timeout: number, messageArray: string[]): void {
    Notifications.titleClearBlink()
    const blinkTask = () => {
      messageArray.forEach((msg: string, i: number) => {
        const h = setTimeout(() => (document.title = msg), timeout * i)
        titleBlinkHandlers.push(h)
      })
    }
    blinkTask()
    const h1 = setInterval(blinkTask, timeout * messageArray.length)
    titleBlinkHandlers.push(h1)
  }

  private static titleBlinkIfTabInactive(messageArray: string[]): void {
    if (tabActive) {
      return
    }
    Notifications.titleBlink(1100, messageArray)
    const callback = () => {
      onActivate.delete(callback)
      Notifications.titleClearBlink()
    }
    onActivate.add(callback)
  }
}
